import React from 'react';
import { AppState, Pressable, View, StyleSheet, Platform, PanResponder } from 'react-native';
import { useIsFocused } from '@react-navigation/native';
import { useTheme } from '@/context/ThemeContext';
import { useAudioPlayer, useAudioPlayerStatus, setAudioModeAsync } from 'expo-audio';
import { Svg, Rect, Line } from 'react-native-svg';
import Icon from '@/components/Icon';
import Text from '@/components/Typography/Text';
import { seekRatioFromClientX } from '@/constants/utils/audio_seek';
import { useAndroidReadAloudPlayer } from '@/lib/useAndroidReadAloudPlayer';

const createStyles = (colors) => StyleSheet.create({
  root: {
    width: '100%',
    height: '100%',
    justifyContent: 'center',
  },
  inner: {
    width: '100%',
    paddingHorizontal: 8,
    gap: 6,
  },
  waveArea: {
    width: '100%',
    alignItems: 'center',
    justifyContent: 'center',
  },
  controlsRow: {
    width: '100%',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    zIndex: 3,
  },
  playButton: {
    width: 32,
    height: 32,
    borderRadius: 16,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(0,0,0,0.55)',
    zIndex: 3,
  },
  volumeButton: {
    width: 32,
    height: 32,
    borderRadius: 16,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(0,0,0,0.35)',
    zIndex: 2,
  },
  speedButton: {
    minWidth: 42,
    height: 32,
    paddingHorizontal: 7,
    borderRadius: 16,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(0,0,0,0.35)',
    zIndex: 2,
  },
  trackWrapper: {
    flex: 1,
    height: 12,
    justifyContent: 'center',
  },
  trackBg: {
    width: '100%',
    height: 6,
    borderRadius: 3,
    backgroundColor: colors.solid_color_secondary,
    // overflow: 'hidden',
    position: 'relative',
  },
  trackFill: {
    height: '100%',
    borderRadius: 3,
    backgroundColor: colors.text_color_primary,
  },
  trackKnob: {
    position: 'absolute',
    top: -4,
    width: 14,
    height: 14,
    borderRadius: 7,
    backgroundColor: colors.text_color_primary,
    ...Platform.select({
      web: { boxShadow: '0px 1px 2px rgba(0, 0, 0, 0.25)' },
      default: {
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 1 },
        shadowOpacity: 0.25,
        shadowRadius: 2,
        elevation: 2,
      },
    }),
  },
  trackGhostKnob: {
    position: 'absolute',
    top: -4,
    width: 14,
    height: 14,
    borderRadius: 7,
    backgroundColor: 'transparent',
    borderWidth: 2,
    borderColor: colors.text_color_primary,
    opacity: 0.6,
    ...Platform.select({
      web: { boxShadow: '0px 1px 2px rgba(0, 0, 0, 0.15)' },
      default: {
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 1 },
        shadowOpacity: 0.15,
        shadowRadius: 2,
        elevation: 1,
      },
    }),
  },
});

const AudioWaveformPlayer = React.forwardRef(function AudioWaveformPlayer({
  sourceUrl,
  size,
  file,
  isLarge = false,
  autoPlay = false,
  initialPositionSeconds = 0,
  playbackRate: controlledPlaybackRate,
  onPlaybackRateChange,
  onPlaybackStart,
  onPlaybackEnd,
  onPlaybackPause,
  onPlaybackStateChange,
  onPlaybackError,
  backgroundPlayback = false,
  deliberateSeek = false,
  suspended = false,
  isFocused = true,
  player,
  status,
}, ref) {
  const { colors } = useTheme();
  const styles = React.useMemo(() => createStyles(colors), [colors]);
  
  // Convert seconds to milliseconds for backward compatibility with UI
  const isPlaying = status?.playing || false;
  const positionMillis = (status?.currentTime || 0) * 1000;
  const durationMillis = (status?.duration || 0) * 1000;
  
  const [volume, setVolume] = React.useState(1);
  const [playbackRequested, setPlaybackRequested] = React.useState(false);
  const [internalPlaybackRate, setInternalPlaybackRate] = React.useState(1);
  const [trackWidth, setTrackWidth] = React.useState(0);
  const [waveWidth, setWaveWidth] = React.useState(0);
  const [isDragging, setIsDragging] = React.useState(false);
  const [dragRatio, setDragRatio] = React.useState(null);
  const [isHovering, setIsHovering] = React.useState(false);
  const [hoverRatio, setHoverRatio] = React.useState(null);
  const trackBgRef = React.useRef(null);
  const dragRatioRef = React.useRef(null);
  const didNotifyEndRef = React.useRef(false);
  const didNotifyStartRef = React.useRef(false);
  const playbackStartedRef = React.useRef(false);
  const initialSeekSourceRef = React.useRef(null);
  const playbackRequestRef = React.useRef(0);
  const sessionRetryTimerRef = React.useRef(null);
  const cancelPlaybackRequest = React.useCallback(() => {
    clearTimeout(sessionRetryTimerRef.current);
    sessionRetryTimerRef.current = null;
    return ++playbackRequestRef.current;
  }, []);
  const initialSeekPromiseRef = React.useRef(Promise.resolve());
  const playbackRate = controlledPlaybackRate ?? internalPlaybackRate;
  const playbackErrorRef = React.useRef(onPlaybackError);
  playbackErrorRef.current = onPlaybackError;
  const tracksPlaybackErrors = typeof onPlaybackError === 'function';

  React.useEffect(() => {
    // Once playback starts, a later OS/headset interruption is a pause, not a
    // failed start. Do not download and restart audio behind the user's back.
    if (isPlaying || suspended) setPlaybackRequested(false);
  }, [isPlaying, playbackRequested, suspended]);

  React.useEffect(() => {
    // ExoPlayer can report isLoaded after loading stops even when play() never
    // starts (and does not throw). Watch the requested playback as well as the
    // load so Android's local-file fallback can recover that silent failure.
    if (!tracksPlaybackErrors || !sourceUrl || suspended ||
      (status?.isLoaded && (!playbackRequested || isPlaying))) return undefined;
    let timer;
    const clear = () => clearTimeout(timer);
    const start = () => {
      clear();
      timer = setTimeout(() => {
        cancelPlaybackRequest();
        setPlaybackRequested(false);
        try { player?.pause(); } catch (_) {}
        const error = new Error(status?.isLoaded ? 'Audio playback did not start' : 'Audio playback did not load');
        error.errorCode = status?.isLoaded ? 'audio_start_timeout' : 'audio_load_timeout';
        playbackErrorRef.current?.(error);
      }, 30000);
    };
    // Mobile suspends timers in the background. Give the CDN a fresh loading
    // window on foregrounding rather than immediately failing on return.
    if (AppState.currentState !== 'background' && AppState.currentState !== 'inactive') start();
    const subscription = AppState.addEventListener('change', state => state === 'active' ? start() : clear());
    return () => { clear(); subscription?.remove?.(); };
  }, [tracksPlaybackErrors, player, sourceUrl, status?.isLoaded, suspended, playbackRequested, isPlaying, cancelPlaybackRequest]);

  const startPlayback = React.useCallback(() => {
    if (!player || suspended) return;
    const request = cancelPlaybackRequest();
    setPlaybackRequested(true);
    let sessionRetries = 0;
    const fail = error => {
      if (request !== playbackRequestRef.current) return;
      setPlaybackRequested(false);
      playbackErrorRef.current?.(error);
    };
    const configurePlaybackMode = () => setAudioModeAsync({ playsInSilentMode: true,
      allowsRecording: false, shouldPlayInBackground: true,
      interruptionMode: 'doNotMix', interruptionModeAndroid: 'doNotMix' });
    const playFailed = error => {
      if (request !== playbackRequestRef.current) return;
      // Expo's iOS play() activates the shared AVAudioSession synchronously.
      // A transient activation failure during a handoff must not discard the
      // current chunk/position. Retry only this error, with 750 ms total backoff.
      if (Platform.OS === 'ios' && backgroundPlayback &&
        /session activation failed/i.test(error?.message || '') && sessionRetries < 2) {
        const delay = [250, 500][sessionRetries++];
        sessionRetryTimerRef.current = setTimeout(() => {
          sessionRetryTimerRef.current = null;
          if (request !== playbackRequestRef.current) return;
          configurePlaybackMode().then(play).catch(fail);
        }, delay);
      } else fail(error);
    };
    const play = () => {
      if (request !== playbackRequestRef.current) return;
      try {
        didNotifyEndRef.current = false;
        didNotifyStartRef.current = false;
        playbackStartedRef.current = false;
        if (player.duration > 0 && player.currentTime >= player.duration) player.seekTo(0);
        const result = player.play();
        // Some adapters reject asynchronously rather than throwing from play.
        if (result?.catch) result.catch(playFailed);
      } catch (error) { playFailed(error); }
    };
    if (player.configureAudioMode) {
      player.configureAudioMode(backgroundPlayback)
        .then(() => initialSeekPromiseRef.current).then(play).catch(fail);
    } else if (backgroundPlayback) {
      configurePlaybackMode()
        .catch(error => { if (Platform.OS === 'ios') throw error; })
        .then(() => initialSeekPromiseRef.current).then(play).catch(fail);
    } else play();
  }, [backgroundPlayback, player, suspended, cancelPlaybackRequest]);

  // Ensure audio plays even when iPhone silent switch is on
  React.useEffect(() => {
    if (suspended) return;
    if (player.configureAudioMode) return;
    setAudioModeAsync({ playsInSilentMode: true, ...(backgroundPlayback && {
      shouldPlayInBackground: true, interruptionMode: 'doNotMix',
      interruptionModeAndroid: 'doNotMix',
    }) }).catch(() => {});
  }, [backgroundPlayback, player, suspended]);

  React.useEffect(() => {
    onPlaybackStateChange?.({ playing: isPlaying, position: status?.currentTime || 0,
      duration: status?.duration || 0, available: !!sourceUrl && !!status?.isLoaded,
      ...(tracksPlaybackErrors && { loading: !!sourceUrl && !status?.isLoaded }) });
  }, [isPlaying, onPlaybackStateChange, sourceUrl, status?.currentTime,
    status?.duration, status?.isLoaded, tracksPlaybackErrors]);

  React.useEffect(() => {
    if (!backgroundPlayback || !player || suspended) return undefined;
    if (Platform.OS !== 'web' || typeof navigator === 'undefined' || !navigator.mediaSession) return undefined;
    const media = navigator.mediaSession;
    if (typeof MediaMetadata !== 'undefined') media.metadata = new MediaMetadata({ title: 'Read aloud', artist: 'Gab AI' });
    const handlers = {
      play: startPlayback,
      pause: () => { cancelPlaybackRequest(); setPlaybackRequested(false); player.pause(); onPlaybackPause?.(); },
      seekbackward: (event) => player.seekTo(Math.max(0, player.currentTime - (event.seekOffset || 15))),
      seekforward: (event) => player.seekTo(Math.min(player.duration, player.currentTime + (event.seekOffset || 15))),
      seekto: (event) => player.seekTo(event.seekTime),
    };
    Object.entries(handlers).forEach(([name, handler]) => {
      try { media.setActionHandler(name, handler); } catch (_) {}
    });
    return () => {
      cancelPlaybackRequest();
      Object.keys(handlers).forEach(name => { try { media.setActionHandler(name, null); } catch (_) {} });
      media.metadata = null;
      media.playbackState = 'none';
    };
  }, [backgroundPlayback, onPlaybackPause, player, startPlayback, suspended, cancelPlaybackRequest]);

  React.useEffect(() => {
    if (!backgroundPlayback || Platform.OS !== 'web' || typeof navigator === 'undefined' || !navigator.mediaSession) return;
    navigator.mediaSession.playbackState = isPlaying ? 'playing' : 'paused';
    if (durationMillis > 0) {
      try { navigator.mediaSession.setPositionState({ duration: durationMillis / 1000,
        position: Math.min(positionMillis, durationMillis) / 1000, playbackRate }); } catch (_) {}
    }
  }, [backgroundPlayback, durationMillis, isPlaying, playbackRate, positionMillis]);

  // Set initial volume when player is ready
  React.useEffect(() => {
    if (player) {
      player.volume = volume;
    }
  }, [player, volume]);

  React.useEffect(() => {
    if (!player) return;
    // The installed iOS 0.4.9 setter also changes AVPlayer.rate and can start
    // a paused player. Apply speed after playback starts.
    if (Platform.OS === 'ios' && !isPlaying) return;
    try {
      player.setPlaybackRate(playbackRate, 'high');
    } catch (_) {}
  }, [isPlaying, playbackRate, player]);

  React.useEffect(() => {
    if (
      !player ||
      !sourceUrl ||
      !status?.isLoaded ||
      initialPositionSeconds <= 0 ||
      initialSeekSourceRef.current === sourceUrl
    ) return;
    try {
      initialSeekPromiseRef.current = Promise.resolve(player.seekTo(initialPositionSeconds));
      initialSeekPromiseRef.current.catch(() => {});
      initialSeekSourceRef.current = sourceUrl;
    } catch (_) {}
  }, [initialPositionSeconds, player, sourceUrl, status?.isLoaded]);

  React.useEffect(() => {
    didNotifyEndRef.current = false;
    didNotifyStartRef.current = false;
    playbackStartedRef.current = false;
    if (
      !autoPlay ||
      suspended ||
      !player ||
      !sourceUrl ||
      (initialPositionSeconds > 0 && !status?.isLoaded)
    ) return undefined;
    const timer = setTimeout(() => {
      try { startPlayback(); } catch (_) {}
    }, 50);
    return () => { clearTimeout(timer); cancelPlaybackRequest(); };
  }, [autoPlay, initialPositionSeconds, player, sourceUrl, startPlayback, status?.isLoaded, suspended, cancelPlaybackRequest]);

  React.useEffect(() => {
    if (suspended) cancelPlaybackRequest();
  }, [suspended, cancelPlaybackRequest]);

  React.useEffect(() => {
    if (status?.playing || status?.currentTime > 0) {
      playbackStartedRef.current = true;
    }
    if (status?.playing && !didNotifyStartRef.current) {
      didNotifyStartRef.current = true;
      onPlaybackStart?.();
    }
    if (!status?.didJustFinish || !playbackStartedRef.current || didNotifyEndRef.current) return;
    didNotifyEndRef.current = true;
    setPlaybackRequested(false);
    try {
      player?.pause();
      player?.seekTo(0);
    } catch (_) {}
    onPlaybackEnd?.();
  }, [onPlaybackEnd, onPlaybackStart, player, status?.currentTime, status?.didJustFinish, status?.playing]);

  // Cleanup: pause audio when component unmounts (e.g., navigating away)
  React.useEffect(() => {
    return () => {
      cancelPlaybackRequest();
      if (player) {
        try {
          player.pause();
        } catch (e) {
          // Ignore cleanup errors
        }
      }
    };
  }, [player, sourceUrl, cancelPlaybackRequest]);

  // Pause playback when the screen loses focus (navigate away)
  React.useEffect(() => {
    if (!backgroundPlayback && !isFocused && player) {
      try {
        player.pause();
      } catch (e) {
        // Ignore focus pause errors
      }
    }
  }, [backgroundPlayback, isFocused, player]);

  const togglePlayPause = React.useCallback(async (e) => {
    if (e?.stopPropagation) e.stopPropagation();
    if (!sourceUrl || !player) {
      console.log('togglePlayPause no sourceUrl or player');
      return;
    }
    try {
      if (isPlaying) {
        cancelPlaybackRequest();
        setPlaybackRequested(false);
        player.pause();
        onPlaybackPause?.();
      } else {
        startPlayback();
      }
    } catch (err) {
      // console.log('togglePlayPause error', err);
    }
  }, [sourceUrl, player, isPlaying, startPlayback, onPlaybackPause, cancelPlaybackRequest]);

  const pauseAudio = React.useCallback(() => {
    cancelPlaybackRequest();
    setPlaybackRequested(false);
    if (!player) return;
    try {
      player.pause();
      onPlaybackPause?.();
    } catch (e) {}
  }, [player, onPlaybackPause, cancelPlaybackRequest]);

  const stopAudio = React.useCallback(() => {
    cancelPlaybackRequest();
    setPlaybackRequested(false);
    if (!player) return;
    try {
      player.pause();
      player.seekTo(0);
    } catch (e) {}
  }, [player, cancelPlaybackRequest]);

  const playAudio = React.useCallback(() => {
    if (!player || !sourceUrl) return;
    try {
      startPlayback();
    } catch (e) {}
  }, [player, sourceUrl, startPlayback]);

  React.useImperativeHandle(ref, () => ({
    play: playAudio,
    pause: pauseAudio,
    stop: stopAudio,
    seekTo: (seconds) => player?.seekTo?.(seconds),
    getPosition: () => status?.currentTime || 0,
    getDuration: () => status?.duration || 0,
    isPlaying: () => isPlaying,
  }), [playAudio, pauseAudio, stopAudio, player, status?.currentTime, status?.duration, isPlaying]);

  const formatTime = React.useCallback((ms) => {
    if (!ms || ms <= 0) return '0:00';
    const totalSeconds = Math.floor(ms / 1000);
    const m = Math.floor(totalSeconds / 60);
    const s = totalSeconds % 60;
    return `${m}:${s < 10 ? '0' + s : s}`;
  }, []);

  const baseBars = size >= 300 ? 64 : 40;
  const numBars = isLarge ? baseBars * 2 : baseBars;
  const progressRatio = durationMillis > 0 ? Math.max(0, Math.min(1, positionMillis / durationMillis)) : 0;
  const displayRatio = isDragging && typeof dragRatio === 'number' ? dragRatio : progressRatio;

  const barData = React.useMemo(() => {
    // Deterministic pseudo-waveform from file identity
    const key = `${file?.name || ''}|${file?.size || file?.sizeInBytes || 0}|${file?.cdnPath || file?.url || ''}`;
    let seed = 0;
    for (let i = 0; i < key.length; i++) {
      seed = (seed * 31 + key.charCodeAt(i)) >>> 0;
    }
    const rand = () => {
      seed ^= seed << 13;
      seed ^= seed >>> 17;
      seed ^= seed << 5;
      return ((seed >>> 0) % 1000) / 1000;
    };
    const out = [];
    for (let i = 0; i < numBars; i++) {
      const base = rand() * 0.8 + 0.2; // 0.2 - 1.0
      const ease = Math.sin((i / numBars) * Math.PI);
      out.push(Math.min(1, base * (0.5 + 0.5 * ease)));
    }
    return out;
  }, [file?.name, file?.size, file?.sizeInBytes, file?.cdnPath, file?.url, numBars]);

  const waveHeight = Math.max(
    20,
    Math.min(isLarge ? 200 : 40, Math.round(size * (isLarge ? 0.18 : 0.12)))
  );
  const volumeIcon = volume === 0 ? 'volume-xmark' : (volume < 0.75 ? 'volume-low' : 'volume-high');

  const cycleVolume = React.useCallback((e) => {
    if (e?.stopPropagation) e.stopPropagation();
    if (!player) return;
    const next = volume === 1 ? 0.5 : (volume === 0.5 ? 0 : 1);
    setVolume(next);
    try {
      player.volume = next;
    } catch (e) {
      // console.log('cycleVolume error', e);
    }
  }, [player, volume]);

  const cyclePlaybackRate = React.useCallback((e) => {
    if (e?.stopPropagation) e.stopPropagation();
    const rates = [0.75, 1, 1.25, 1.5, 2];
    const currentIndex = rates.indexOf(playbackRate);
    const next = rates[(currentIndex + 1) % rates.length];
    if (controlledPlaybackRate == null) setInternalPlaybackRate(next);
    onPlaybackRateChange?.(next);
  }, [controlledPlaybackRate, onPlaybackRateChange, playbackRate]);

  const updateDragFromX = React.useCallback((x, width) => {
    if (!width || !durationMillis) return;
    const clampedX = Math.max(0, Math.min(width, x));
    const ratio = clampedX / width;
    dragRatioRef.current = ratio;
    setDragRatio(ratio);
  }, [durationMillis]);

  const commitSeekFromRatio = React.useCallback((ratio) => {
    if (!player || !durationMillis) return;
    try {
      // Convert to seconds for expo-audio
      const durationSeconds = durationMillis / 1000;
      const targetSeconds = Math.max(0, Math.min(durationSeconds, durationSeconds * ratio));
      player.seekTo(targetSeconds);
    } catch (e) {
      // console.log('commitSeekFromRatio error', e);
    }
  }, [player, durationMillis]);

  const finishDrag = React.useCallback(async () => {
    setIsDragging(false);
    const ratio = typeof dragRatioRef.current === 'number' ? dragRatioRef.current : progressRatio;
    dragRatioRef.current = null;
    setDragRatio(null);
    await commitSeekFromRatio(ratio);
  }, [commitSeekFromRatio, progressRatio]);

  const cancelDrag = React.useCallback(() => {
    setIsDragging(false);
    dragRatioRef.current = null;
    setDragRatio(null);
  }, []);

  const swallowCardPress = React.useCallback((e) => {
    e?.stopPropagation?.();
    e?.nativeEvent?.stopPropagation?.();
  }, []);

  const seekFromWebClick = React.useCallback((e, widthFallback) => {
    swallowCardPress(e);
    if (Platform.OS !== 'web' || deliberateSeek) return;
    const rect = e.currentTarget?.getBoundingClientRect?.();
    const ratio = seekRatioFromClientX(e.clientX, rect || { left: 0, width: widthFallback });
    if (ratio == null) return;
    dragRatioRef.current = ratio;
    setDragRatio(ratio);
    commitSeekFromRatio(ratio);
  }, [commitSeekFromRatio, deliberateSeek, swallowCardPress]);

  const trackResponder = React.useMemo(() => PanResponder.create({
    onStartShouldSetPanResponder: () => !deliberateSeek,
    onMoveShouldSetPanResponder: (_, gesture) => !deliberateSeek || (Math.abs(gesture.dx) >= 12 && Math.abs(gesture.dx) > Math.abs(gesture.dy)),
    onPanResponderGrant: (evt) => {
      setIsDragging(true);
      updateDragFromX(evt.nativeEvent.locationX, trackWidth);
    },
    onPanResponderMove: (evt) => {
      updateDragFromX(evt.nativeEvent.locationX, trackWidth);
    },
    onPanResponderRelease: finishDrag,
    onPanResponderTerminate: cancelDrag,
  }), [cancelDrag, deliberateSeek, finishDrag, trackWidth, updateDragFromX]);

  const waveResponder = React.useMemo(() => PanResponder.create({
    onStartShouldSetPanResponder: () => !deliberateSeek,
    onMoveShouldSetPanResponder: (_, gesture) => !deliberateSeek || (Math.abs(gesture.dx) >= 12 && Math.abs(gesture.dx) > Math.abs(gesture.dy)),
    onPanResponderGrant: (evt) => {
      setIsDragging(true);
      updateDragFromX(evt.nativeEvent.locationX, waveWidth);
    },
    onPanResponderMove: (evt) => {
      updateDragFromX(evt.nativeEvent.locationX, waveWidth);
    },
    onPanResponderRelease: finishDrag,
    onPanResponderTerminate: cancelDrag,
  }), [cancelDrag, deliberateSeek, finishDrag, updateDragFromX, waveWidth]);

  if (Platform.OS === 'macos' && status?.error) {
    return (
      <View style={styles.root}>
        <View style={[styles.inner, { alignItems: 'center' }]}>
          <Text size="xs" color="secondary">Could not load audio.</Text>
          <Pressable
            accessibilityRole="button"
            accessibilityLabel="Retry audio"
            onPress={(event) => { event?.stopPropagation?.(); player.replace(sourceUrl); }}
            style={{ paddingHorizontal: 12, paddingVertical: 8 }}
          >
            <Text size="xs">Retry audio</Text>
          </Pressable>
        </View>
      </View>
    );
  }

  return (
    <View style={styles.root}>
      <View style={styles.inner}>
        <View
          style={[styles.waveArea, { height: waveHeight }]}
          onLayout={(e) => setWaveWidth(Math.round(e.nativeEvent.layout.width))}
          onClick={(e) => seekFromWebClick(e, waveWidth)}
          {...waveResponder.panHandlers}
        >
          <Svg
            width={size - 24}
            height={waveHeight}
            viewBox={`0 0 ${size - 24} ${waveHeight}`}
            pointerEvents="none"
          >
            {(() => {
              const width = size - 24;
              const height = waveHeight;
              const maxBarHeight = height * 0.9;
              const barWidth = width / numBars * 0.6;
              const gap = (width - barWidth * numBars) / (numBars + 1);
              const baseline = height / 2;
              const elems = [];
              for (let i = 0; i < numBars; i++) {
                const x = gap + i * (barWidth + gap);
                const h = Math.max(3, Math.round(maxBarHeight * barData[i]));
                const y = baseline - h / 2;
                const isPast = (i / numBars) <= displayRatio;
                elems.push(
                  <Rect
                    key={`bar-${i}`}
                    x={x}
                    y={y}
                    width={barWidth}
                    height={h}
                    rx={barWidth * 0.3}
                    fill={isPast ? colors.text_color_primary : colors.text_color_secondary}
                    opacity={isPast ? 0.95 : 0.6}
                  />
                );
              }
              const px = gap + Math.max(0, Math.min(numBars, Math.round(numBars * displayRatio))) * (barWidth + gap);
              elems.push(
                <Line key="progress-line" x1={px} x2={px} y1={height * 0.15} y2={height * 0.85} stroke={colors.text_color_alt} strokeWidth={2} opacity={0.8} />
              );
              return elems;
            })()}
          </Svg>
        </View>
        <View style={styles.controlsRow}>
          <Pressable style={styles.playButton} onPress={togglePlayPause} accessibilityRole="button" accessibilityLabel={isPlaying ? 'Pause audio' : 'Play audio'}>
            <Icon name={isPlaying ? 'pause' : 'play'} size={16} color="#fff" />
          </Pressable>
          <View style={styles.trackWrapper}>
            <View
              style={styles.trackBg}
              ref={trackBgRef}
              onLayout={(e) => setTrackWidth(Math.round(e.nativeEvent.layout.width))}
            >
              <View style={[styles.trackFill, { width: `${displayRatio * 100}%` }]} />
              {!!trackWidth && (
                <View
                  style={[styles.trackKnob, { left: Math.max(0, Math.min(trackWidth - 14, (displayRatio * trackWidth) - 7)) }]}
                />
              )}
              {/* Hover overlay and drag handlers */}
              <View
                style={StyleSheet.absoluteFill}
                accessible
                accessibilityRole="adjustable"
                accessibilityLabel="Audio position"
                // Android Fabric converts these values to native integers.
                // Fractional seconds (e.g. 2.351) throw before audio can play.
                // Keep the numeric range in whole milliseconds and give screen
                // readers the same human-readable time used by the controls.
                accessibilityValue={{ min: 0, max: Math.round(durationMillis),
                  now: Math.round(Math.max(0, Math.min(positionMillis, durationMillis))),
                  text: `${formatTime(positionMillis)} / ${formatTime(durationMillis)}` }}
                accessibilityActions={[{ name: 'increment' }, { name: 'decrement' }]}
                onAccessibilityAction={event => player?.seekTo(Math.max(0, Math.min(durationMillis / 1000,
                  positionMillis / 1000 + (event.nativeEvent.actionName === 'increment' ? 15 : -15))))}
                {...trackResponder.panHandlers}
                onClick={(e) => seekFromWebClick(e, trackWidth)}
                onMouseEnter={() => { if (Platform.OS === 'web') setIsHovering(true); }}
                onMouseLeave={() => { if (Platform.OS === 'web') { setIsHovering(false); setHoverRatio(null); } }}
                onMouseMove={(e) => {
                  if (Platform.OS !== 'web') return;
                  try {
                    const rect = e.currentTarget.getBoundingClientRect?.();
                    const x = typeof e.clientX === 'number' && rect ? (e.clientX - rect.left) : 0;
                    const w = rect?.width || trackWidth || 0;
                    if (w > 0) {
                      const r = Math.max(0, Math.min(1, x / w));
                      setHoverRatio(r);
                    }
                  } catch (err) {
                    // console.log('onMouseMove error', err);
                  }
                }}
              />
              {Platform.OS === 'web' && isHovering && !isDragging && typeof hoverRatio === 'number' && !!trackWidth && (
                <View
                  style={[styles.trackGhostKnob, { left: Math.max(0, Math.min(trackWidth - 14, (hoverRatio * trackWidth) - 7)) }]}
                  pointerEvents="none"
                />
              )}
            </View>
          </View>
          <Pressable style={styles.volumeButton} onPress={cycleVolume} accessibilityRole="button" accessibilityLabel={`Change volume, currently ${Math.round(volume * 100)} percent`}>
            <Icon name={volumeIcon} size={16} color="#fff" />
          </Pressable>
          <Pressable
            style={styles.speedButton}
            onPress={cyclePlaybackRate}
            accessibilityRole="button"
            accessibilityLabel={`Playback speed ${playbackRate} times`}
          >
            <Text size="xxs" style={{ color: '#fff' }}>{playbackRate}×</Text>
          </Pressable>
        </View>
        {Platform.OS === 'macos' && isLarge ? (
          <View style={{ flexDirection: 'row', justifyContent: 'space-between', paddingHorizontal: 12, marginTop: 8 }}>
            <Text size="xs" color="secondary" style={{ fontVariant: ['tabular-nums'] }}>
              {formatTime(positionMillis)}
            </Text>
            <Text size="xs" color="secondary" style={{ fontVariant: ['tabular-nums'] }}>
              {formatTime(durationMillis)}
            </Text>
          </View>
        ) : null}
      </View>
    </View>
  );
});

const ExpoAudioPlayer = React.forwardRef(function ExpoAudioPlayer(props, ref) {
  const player = useAudioPlayer(props.sourceUrl || undefined);
  const status = useAudioPlayerStatus(player);
  return <AudioWaveformPlayer {...props} player={player} status={status} ref={ref} />;
});

const AndroidReadAloudPlayer = React.forwardRef(function AndroidReadAloudPlayer(props, ref) {
  const { player, status } = useAndroidReadAloudPlayer(props.sourceUrl, props.onPlaybackError);
  return <AudioWaveformPlayer {...props} player={player} status={status} ref={ref} />;
});

const PlayerImplementation = React.forwardRef(function PlayerImplementation(props, ref) {
  const Component = Platform.OS === 'android' && props.androidMediaPlayer
    ? AndroidReadAloudPlayer : ExpoAudioPlayer;
  return <Component {...props} ref={ref} />;
});

// The persistent player lives above the route navigator. Only ordinary
// attachments need a route navigation object and useIsFocused subscription.
const FocusAwareAudioPlayer = React.forwardRef(function FocusAwareAudioPlayer(props, ref) {
  const isFocused = useIsFocused();
  return <PlayerImplementation {...props} isFocused={isFocused} ref={ref} />;
});

export default React.forwardRef(function AudioPlayer(props, ref) {
  return props.backgroundPlayback
    ? <PlayerImplementation {...props} ref={ref} />
    : <FocusAwareAudioPlayer {...props} ref={ref} />;
});
